import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, AlertTriangle, Radar as RadarIcon, CheckCircle2 } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SidebarNav } from './SidebarNav';
import { fmtRelative, CURRENCY_META, SUPPORTED_CURRENCIES } from '@/lib/format';
import { useCurrency } from '@/context/CurrencyContext';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

export const Topbar = ({ title }) => {
  const navigate = useNavigate();
  const { currency, setCurrency } = useCurrency();
  const [notes, setNotes] = useState([]);
  const unread = notes.filter((n) => !n.read).length;

  useEffect(() => { api.get('/notifications').then((r) => setNotes(r.data)).catch(() => {}); }, []);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#ECEEF2] bg-white/85 backdrop-blur-md px-4 sm:px-6">
      <Sheet>
        <SheetTrigger asChild>
          <button className="lg:hidden p-2 -ml-2 text-slate-600" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72 border-r-0"><SidebarNav /></SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0">
        <h1 className="text-[17px] font-semibold text-ink-900 truncate font-display" data-testid="page-title">{title}</h1>
      </div>

      {/* Global currency switcher — affects every monetary figure across the app */}
      <Select value={currency} onValueChange={setCurrency}>
        <SelectTrigger
          className="h-9 w-[104px] rounded-lg border-[#ECEEF2] bg-white text-sm font-medium text-ink-900 focus:ring-brand/30"
          data-testid="currency-switcher">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end" className="rounded-xl border-[#ECEEF2] shadow-lifted">
          {SUPPORTED_CURRENCIES.map((code) => (
            <SelectItem key={code} value={code} data-testid={`currency-option-${code}`} className="cursor-pointer">
              <span className="inline-flex items-center gap-2">
                <span className="font-mono text-slate-500 w-4 text-center">
                  {CURRENCY_META[code].symbol.trim() && CURRENCY_META[code].symbol.trim() !== code ? CURRENCY_META[code].symbol.trim() : ''}
                </span>
                <span className="font-semibold">{code}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="relative p-2 rounded-lg hover:bg-secondary text-slate-600 transition-colors" data-testid="notifications-btn">
            <Bell className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-danger ring-2 ring-white" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 rounded-xl border-[#ECEEF2] shadow-lifted">
          <DropdownMenuLabel className="flex items-center justify-between">
            Notifications {unread > 0 && <Badge className="bg-danger/10 text-danger-700 hover:bg-danger/10">{unread} new</Badge>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {notes.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500">No notifications</div>}
          {notes.slice(0, 8).map((n) => {
            const Icon = n.severity === 'high' || n.severity === 'warning' ? AlertTriangle : (n.type === 'detection' ? RadarIcon : CheckCircle2);
            const color = n.severity === 'high' ? 'text-danger' : n.severity === 'warning' ? 'text-warn' : 'text-brand';
            return (
              <DropdownMenuItem key={n.id} className="flex items-start gap-2.5 py-2.5 cursor-pointer rounded-lg"
                onClick={() => { if (n.link) navigate(n.link); }}>
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', color)} />
                <div className="min-w-0">
                  <div className={cn('text-xs font-semibold truncate', n.read ? 'text-slate-600' : 'text-ink-900')}>{n.title}</div>
                  <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{fmtRelative(n.created_at)}</div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
